import React from "react";
import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import { PRICING } from "@/constants/testIds";

const TIERS = [
  {
    id: "100",
    name: "Signature",
    drones: "100 drones",
    price: 8000,
    duration: "10-15 min",
    description: "Idéal mariages intimistes, demandes en mariage et événements privés.",
    features: [
      "Show personnalisé sur-mesure",
      "Coordination FAA/DGAC complète",
      "Équipe technique 4 personnes",
      "Répétition générale incluse",
    ],
    highlight: false,
    cta: "Réserver",
  },
  {
    id: "200",
    name: "Premium",
    drones: "200 drones",
    price: 14500,
    duration: "10-15 min",
    description: "Le format le plus demandé pour entreprises, festivals et grands mariages.",
    features: [
      "Show signature 10-15 minutes",
      "Animations cinématiques 3D",
      "Synchronisation musicale pro",
      "Réalisation aftermovie",
      "Repérage site inclus",
    ],
    highlight: true,
    cta: "Choisir Premium",
  },
  {
    id: "500",
    name: "Cinéma",
    drones: "500 drones",
    price: 35000,
    originalPrice: 50000,
    duration: "10-15 min",
    description: "Production cinématographique pour festivals, marques et villes.",
    features: [
      "Show épique 10-15 minutes",
      "Animations 3D haute densité",
      "Logo & branding intégrés",
      "Production audio originale",
      "Diffusion live streaming",
      "Aftermovie 4K cinéma",
      "Direction artistique dédiée",
    ],
    highlight: false,
    discount: 30,
    cta: "Demander un devis",
  },
];

export const Pricing = () => {
  const scrollToContact = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="pricing"
      data-testid={PRICING.root}
      className="relative py-28 md:py-32"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,122,255,0.08),transparent_60%)]" />
      <div className="relative mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-xs uppercase tracking-[0.2em] text-white/70">
            <Sparkles size={12} className="text-[#007AFF]" /> Tarifs transparents
          </div>
          <h2 className="font-display mt-6 text-4xl md:text-5xl lg:text-6xl font-light tracking-tight">
            Trois formats, <span className="italic font-semibold">un</span> ciel sans limite.
          </h2>
          <p className="mt-5 text-white/60 max-w-2xl">
            Des forfaits clé-en-main pour 100, 200 ou 500 drones. Tous nos tarifs incluent
            scénarisation, déclarations DGAC et équipe technique.
          </p>
        </div>

        <div className="mt-16 grid gap-6 lg:grid-cols-3 lg:gap-8">
          {TIERS.map((t, i) => (
            <motion.div
              key={t.id}
              data-testid={PRICING.card(t.id)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
              className={`relative rounded-3xl p-px ${t.highlight ? "beam-border" : ""}`}
            >
              <div
                className={`relative h-full rounded-[calc(theme(borderRadius.3xl)-1px)] p-8 md:p-10 flex flex-col ${
                  t.highlight
                    ? "bg-[#0a0a0c] border border-[#007AFF]/40"
                    : "glass-strong"
                }`}
              >
                {t.highlight && (
                  <div className="absolute -top-3 left-8 inline-flex items-center gap-1 rounded-full bg-[#007AFF] px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-white font-semibold">
                    Le plus choisi
                  </div>
                )}
                {t.discount && (
                  <div className="absolute -top-3 right-8 inline-flex items-center gap-1 rounded-full bg-white text-black px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold">
                    -{t.discount}%
                  </div>
                )}

                <div className="text-xs uppercase tracking-[0.25em] text-white/50">{t.drones}</div>
                <h3 className="font-display mt-2 text-3xl md:text-4xl font-semibold">{t.name}</h3>
                <p className="mt-3 text-sm text-white/60 leading-relaxed">{t.description}</p>

                <div className="mt-7">
                  <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-1">À partir de</div>
                  <div className="flex items-end gap-2">
                    <div className="font-display text-5xl md:text-6xl font-light tracking-tighter">
                      {t.price.toLocaleString("fr-FR")}
                      <span className="text-[#007AFF]">€</span>
                    </div>
                    {t.originalPrice && (
                      <div className="mb-3 text-base text-white/40 line-through">
                        {t.originalPrice.toLocaleString("fr-FR")}€
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-xs text-white/40 mt-1">Durée du show · {t.duration}</div>

                <ul className="mt-8 space-y-3 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-white/80">
                      <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#007AFF]/15 text-[#007AFF]">
                        <Check size={12} />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={PRICING.cta(t.id)}
                  onClick={scrollToContact}
                  className={`mt-10 inline-flex items-center justify-center rounded-full px-6 py-3.5 text-sm font-semibold transition-all ${
                    t.highlight
                      ? "bg-[#007AFF] text-white hover:shadow-[0_0_32px_rgba(0,122,255,0.55)]"
                      : "bg-white/8 text-white border border-white/15 hover:bg-white/15"
                  }`}
                >
                  {t.cta} →
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="mt-10 text-center text-xs text-white/40">
          Tarifs indicatifs HT · Devis personnalisé sous 24h · Acompte 30% à la réservation
        </p>
      </div>
    </section>
  );
};

export default Pricing;
